
import React, { Component } from 'react';

import { translate } from '../../base/i18n';
import { IconUserGroup } from '../../base/icons';
import { getLocalParticipant, getParticipantById } from '../../base/participants/functions';
import { BaseIndicator } from '../../base/react';
import { connect } from '../../base/redux';
import { areDeviceIndicatorsEnabled, getParticipantSessions } from '../functions';

/**
 * The type of the React {@code Component} props of {@link MultiSessionIndicator}.
 */
type Props = {

    /**
     * Labels of the person's other endpoints, used for the tooltip.
     */
    _otherDevices: Array<string>,

    /**
     * How many endpoints the person behind this tile has in the conference.
     */
    _sessionCount: number,

    /**
     * From which side of the indicator the tooltip should appear from.
     */
    tooltipPosition: string
};

/**
 * Marks a tile whose participant is the same person as another tile, so that
 * two tiles with one name do not read as two different people.
 *
 * @augments Component
 */
class MultiSessionIndicator extends Component<Props> {
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const { _otherDevices, _sessionCount, t, tooltipPosition } = this.props;

        if (_sessionCount < 2) {
            return null;
        }

        const tooltip = _otherDevices.length
            ? t('sessionPersistence.alsoOnNamedDevices', { devices: _otherDevices.join(', ') })
            : t('sessionPersistence.alsoOnOtherDevices', { count: _sessionCount - 1 });

        return (
            <div className = 'multi-session-icon right'>
                <BaseIndicator
                    className = 'multisessionindicator toolbar-icon'
                    icon = { IconUserGroup }
                    iconSize = { 15 }
                    tooltipKey = { tooltip }
                    tooltipPosition = { tooltipPosition || 'bottom-right' } />
            </div>
        );
    }
}

/**
 * Maps (parts of) the redux state to the props of this component.
 *
 * @param {Object} state - The redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps) {
    const { participantID } = ownProps;

    // Only the local participant has no id until the conference is joined.
    const participant = participantID
        ? getParticipantById(state, participantID)
        : getLocalParticipant(state);
    const sessions = areDeviceIndicatorsEnabled(state)
        ? getParticipantSessions(state, participant)
        : [];

    return {
        _otherDevices: sessions
            .filter(p => p.id !== participant?.id && p.deviceLabel)
            .map(p => p.deviceLabel),
        _sessionCount: sessions.length
    };
}

export default connect(_mapStateToProps)(translate(MultiSessionIndicator));
